import { useToast } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

interface SwitchOrganizationResponse {
    sessionId: string;
    organizationId: string;
    organizationName: string;
    /** Landing route for the tabs the new org exposes. */
    root: string;
}

interface SwitchOrganizationInput {
    organizationId: string;
    /** Where to land instead of the new org's default route, e.g. the order that was tagged. */
    destination?: { to: string; state?: unknown; };
}

/**
 * Moves a Yaxle employee into another customer org. The API moves their account and
 * issues a new session; adopting it clears every org-scoped cache and filter, and the
 * page moves to the new org's landing route (whatever was open belonged to the old org).
 */
export const useSwitchOrganization = () => {
    const sessionContext = useContext(SessionContext);
    const navigate = useNavigate();
    const toast = useToast();

    return useMutation({
        mutationFn: async ({ organizationId }: SwitchOrganizationInput) => {
            const { data } = await axios.post<SwitchOrganizationResponse>(
                `${API_URL}/internal/switch-organization`,
                { organizationId },
                { headers: { Authorization: sessionContext?.sessionId ?? '' } },
            );
            return data;
        },
        onSuccess: (data, { destination }) => {
            if (!sessionContext?.isAuthenticated) return;
            sessionContext.adoptSwitchedSession(data.sessionId);
            if (destination) {
                navigate(destination.to, destination.state ? { state: destination.state } : undefined);
            } else {
                navigate(data.root);
            }
            toast({ title: `Switched to ${data.organizationName}`, status: 'success', duration: 2500 });
        },
        onError: () => {
            toast({ title: 'Could not switch organizations', status: 'error', duration: 4000 });
        },
    });
};
